package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.LValueAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.semantic.`native`.HostClassNames

/**
 * Stage 1：close 块同步限制语义检查（DSL_E640..DSL_E645）。
 *
 * close body 必须能在销毁那一帧同步跑完，因此禁用所有可能阻塞或跨帧的语句：
 * - DSL_E640：禁止 await（任意位置）
 * - DSL_E641：禁止 delay() 调用
 * - DSL_E642：禁止裸 net.request；`await net.request` 由 DSL_E640 优先拦下，不再重复报
 * - DSL_E643：禁止 state 字段赋值（state 由框架自动释放）
 * - DSL_E644：禁止 local.openScreen / local.back 等导航操作
 * - DSL_E645：禁止调用 screen 级 suspend fun
 *
 * 检查范围：仅 [ScreenAst.closeBody]。其它块（fun / open / action 等）由各自检查器负责。
 *
 * 实现：递归遍历 stmt / expr 树；遇到嵌套 lambda / FunDecl 的内层 body 不再下沉
 * （内层属于其它执行上下文，由 SuspendKeywordChecker 等其它检查器各自把关）。
 */
class CloseBlockChecker(private val diag: DiagnosticCollector) {

    fun check(screen: ScreenAst) {
        val body = screen.closeBody ?: return
        // 收集 screen 级 suspend fun 名集合：close 体内禁止调用其中任何一个
        val suspendFunNames: Set<String> = screen.funs.asSequence()
            .filter { it.isSuspend }
            .map { it.name }
            .toSet()
        val ctx = Ctx(suspendFunNames)
        body.forEach { checkStmt(it, ctx) }
    }

    private data class Ctx(val suspendFunNames: Set<String>)

    // ===== 语句遍历 =====

    private fun checkStmt(s: StmtAst, ctx: Ctx) {
        when (s) {
            is StmtAst.AssignStmt -> {
                // DSL_E643：state 字段赋值
                val lhs = s.lhs
                if (lhs is LValueAst.StateLValue) {
                    diag.error(DslError.E643_CLOSE_FORBID_STATE_ASSIGN, s.position, lhs.name)
                }
                // 即使 lhs 是 state，也仍要扫 rhs（rhs 可能含 await 等）
                checkExpr(s.rhs, ctx)
            }
            is StmtAst.VarDeclStmt -> checkExpr(s.initializer, ctx)
            is StmtAst.ExprStmt -> checkExpr(s.call, ctx)
            is StmtAst.IfStmt -> {
                checkExpr(s.cond, ctx)
                s.thenBody.forEach { checkStmt(it, ctx) }
                s.elseBody.forEach { checkStmt(it, ctx) }
            }
            is StmtAst.WhileStmt -> {
                checkExpr(s.cond, ctx)
                s.body.forEach { checkStmt(it, ctx) }
            }
            is StmtAst.ForInStmt -> {
                checkExpr(s.iterable, ctx)
                s.body.forEach { checkStmt(it, ctx) }
            }
            is StmtAst.WhenStmt -> checkExpr(s.whenExpr, ctx)
            is StmtAst.ReturnStmt -> s.value?.let { checkExpr(it, ctx) }
            is StmtAst.TryStmt -> {
                s.tryBody.forEach { checkStmt(it, ctx) }
                s.catchBody.forEach { checkStmt(it, ctx) }
                s.finallyBody.forEach { checkStmt(it, ctx) }
            }
            // batch 语句：递归检查 body（与 TryStmt 的 tryBody 处理一致）
            is StmtAst.BatchStmt -> s.body.forEach { checkStmt(it, ctx) }
            // 嵌套 fun 声明：不递归进入其 body（属于另一执行上下文）。
            is StmtAst.FunDeclStmt -> Unit
            is StmtAst.BreakStmt, is StmtAst.ContinueStmt -> Unit
        }
    }

    // ===== 表达式遍历 =====

    private fun checkExpr(e: ExprAst, ctx: Ctx) {
        when (e) {
            // DSL_E640：await 出现即报错；不再下沉到 netRequestCall（避免重复报 E642）
            is ExprAst.AwaitExpr -> {
                diag.error(DslError.E640_CLOSE_FORBID_AWAIT, e.position)
            }
            // DSL_E642：裸 net.request 调用
            is ExprAst.NetRequestCall -> {
                diag.error(DslError.E642_CLOSE_FORBID_NET_REQUEST, e.position)
                e.payload.forEach { checkExpr(it.value, ctx) }
                e.callback?.let { checkExpr(it, ctx) }
                e.timeoutMs?.let { checkExpr(it, ctx) }
            }
            // DSL_E641：delay() 内建调用
            is ExprAst.BuiltinCallExpr -> {
                if (e.name == "delay") {
                    diag.error(DslError.E641_CLOSE_FORBID_DELAY, e.position)
                }
                e.args.forEach { checkExpr(it, ctx) }
                e.namedArgs.forEach { checkExpr(it.value, ctx) }
            }
            // DSL_E644：local.<method>(...) 导航/控制 API
            is ExprAst.MemberCall -> {
                val localMethod = localMethodNameOrNull(e)
                if (localMethod != null) {
                    diag.error(DslError.E644_CLOSE_FORBID_LOCAL_NAV, e.position, localMethod)
                }
                checkExpr(e.target, ctx)
                e.args.forEach { checkExpr(it, ctx) }
                e.namedArgs.forEach { checkExpr(it.value, ctx) }
            }
            is ExprAst.SafeMemberCall -> {
                checkExpr(e.target, ctx)
                e.args.forEach { checkExpr(it, ctx) }
            }
            // DSL_E645：调用 screen 级 suspend fun（CallExpr 的 callee 通常是 FunRefExpr）
            is ExprAst.CallExpr -> {
                val targetName = suspendCallTargetName(e, ctx)
                if (targetName != null) {
                    diag.error(DslError.E645_CLOSE_FORBID_SUSPEND_CALL, e.position, targetName)
                }
                checkExpr(e.callee, ctx)
                e.args.forEach { checkExpr(it, ctx) }
                e.namedArgs.forEach { checkExpr(it.value, ctx) }
            }
            is ExprAst.BinaryOp -> {
                checkExpr(e.left, ctx); checkExpr(e.right, ctx)
            }
            // Stage B：net.subscribe 在 close 块允许（不阻塞、不写 state、不依赖响应式）
            is ExprAst.NetSubscribeCall -> checkExpr(e.handler, ctx)
            is ExprAst.UnaryOp -> checkExpr(e.operand, ctx)
            is ExprAst.MemberAccess -> checkExpr(e.target, ctx)
            is ExprAst.SafeMemberAccess -> checkExpr(e.target, ctx)
            // M10 Stage 6：`target!!` 透传 target
            is ExprAst.NonNullAssert -> checkExpr(e.target, ctx)
            is ExprAst.Elvis -> {
                checkExpr(e.left, ctx); checkExpr(e.right, ctx)
            }
            is ExprAst.Ternary -> {
                checkExpr(e.cond, ctx)
                checkExpr(e.thenExpr, ctx)
                checkExpr(e.elseExpr, ctx)
            }
            is ExprAst.IfExpr -> {
                checkExpr(e.cond, ctx)
                e.thenBody.forEach { checkStmt(it, ctx) }
                e.elseBody.forEach { checkStmt(it, ctx) }
            }
            is ExprAst.Interp -> e.parts.forEach {
                if (it is ExprAst.Interp.InterpPart.ExprPart) checkExpr(it.expr, ctx)
            }
            is ExprAst.ListLiteralExpr -> e.elements.forEach { checkExpr(it, ctx) }
            is ExprAst.MapLiteralExpr -> e.entries.forEach { checkExpr(it.value, ctx) }
            is ExprAst.RangeExpr -> {
                checkExpr(e.start, ctx); checkExpr(e.endValue, ctx)
            }
            is ExprAst.IndexExpr -> {
                checkExpr(e.target, ctx); checkExpr(e.index, ctx)
            }
            is ExprAst.WhenExpr -> {
                checkExpr(e.subject, ctx)
                e.branches.forEach { br ->
                    br.values.forEach { checkExpr(it, ctx) }
                    br.body.forEach { checkStmt(it, ctx) }
                }
                e.elseBody?.forEach { checkStmt(it, ctx) }
            }
            is ExprAst.IsExpr -> checkExpr(e.target, ctx)
            // lambda：不深入其 body（属于另一执行上下文，由其它检查器把关）
            is ExprAst.LambdaExpr -> Unit
            // 叶子节点
            is ExprAst.Literal, is ExprAst.NullLit, is ExprAst.IdentRef,
            is ExprAst.LocalRef, is ExprAst.StateRef, is ExprAst.SessionRef, is ExprAst.PersistRef, is ExprAst.DerivedRef, is ExprAst.PropsRef,
            is ExprAst.ThemeRef, is ExprAst.FunRefExpr -> Unit
        }
    }

    /**
     * 判断成员调用是否为 local.<method>：target 为 IdentRef("local")，返回方法名；否则 null。
     */
    private fun localMethodNameOrNull(e: ExprAst.MemberCall): String? {
        val tgt = e.target
        return if (tgt is ExprAst.IdentRef && tgt.name == HostClassNames.LOCAL) e.method else null
    }

    /**
     * 判断 [CallExpr] 是否调用了 screen 级 suspend fun，返回目标 fun 名；否则 null。
     *
     * callee 形态：
     * - [ExprAst.FunRefExpr]：parser 在识别 screen 级 fun 时升格出的引用
     * - [ExprAst.IdentRef]：兜底场景（极少见）
     */
    private fun suspendCallTargetName(e: ExprAst.CallExpr, ctx: Ctx): String? {
        val name = when (val callee = e.callee) {
            is ExprAst.FunRefExpr -> callee.name
            is ExprAst.IdentRef -> callee.name
            else -> return null
        }
        return if (name in ctx.suspendFunNames) name else null
    }
}
